<template>
	<!-- Drag-and-drop text / QR placement on a postcard side (auto campaign editor UX); edits a draft so Close discards them. -->
	<div class="modal fade postcard-pattern-editor" :id="modalId" tabindex="-1" :aria-labelledby="`${modalId}-label`" aria-hidden="true" ref="modal">
		<div class="modal-dialog modal-fullscreen-lg-down modal-dialog-centered template-modal">
			<div class="modal-content">
				<div class="modal-header pattern-modal-header justify-content-start">
					<div>
						<h5 class="modal-title pattern-modal-title" :id="`${modalId}-label`">Template pattern editor</h5>
						<small class="pattern-modal-sub">Place dynamic text on the {{ side }} image. Click the canvas to add it, then drag to fine-tune.</small>
					</div>
					<button type="button" class="pattern-modal-close" data-bs-dismiss="modal" aria-label="Close"><span aria-hidden="true">×</span></button>
				</div>
				<div class="modal-body template-modal-body">
					<div class="template-image-main-container">
						<div class="template-image-scroll-container">
							<div class="template-image-container" @click="handleImageClick">
								<img :src="image" :alt="`${side} template`" class="template-preview-image" ref="templateImage" @load="$forceUpdate()" />
								<div
									v-for="(pattern, index) in patterns"
									:key="index"
									class="text-pattern-overlay"
									:style="{
										left: pattern.x + '%',
										top: pattern.y + '%',
										fontSize: displaySize(pattern.fontSize) + 'px',
										color: pattern.color,
										fontFamily: fontFamily(pattern.fontFamily),
										fontWeight: pattern.fontWeight,
										WebkitTextStroke: textStroke(pattern),
									}"
									:class="{
										selected: selectedIndex === index,
										dragging: drag.active && drag.index === index,
										'legacy-coordinate-pattern': (pattern.coordinateVersion || 1) < 2 && pattern.text !== qrToken,
									}"
									@click.stop="selectedIndex = index"
								>
									<div
										class="pattern-drag-handle"
										:class="{ visible: hoveredIndex === index || selectedIndex === index }"
										@mousedown="startDrag($event, index)"
										@mouseenter="hoveredIndex = index"
										@mouseleave="hoveredIndex = drag.active ? hoveredIndex : null"
									>
										<i class="fad fa-arrows-alt"></i>
									</div>
									<span
										v-if="pattern.text === qrToken"
										class="pattern-qr-preview"
										:style="{ width: displaySize(pattern.qrSize || 220) + 'px', height: displaySize(pattern.qrSize || 220) + 'px' }"
										><i class="fas fa-qrcode"></i
									></span>
									<span v-else class="pattern-text-content">{{ previewText(pattern.text) }}</span>
								</div>
							</div>
						</div>
					</div>

					<div class="template-sidebar">
						<div class="pattern-sidebar-heading">Pattern configuration</div>
						<div class="pattern-sidebar-sub">Build the content, choose variables, then position it on the image.</div>

						<div class="pattern-section mb-3">
							<label class="form-label">Add Pattern Text</label>
							<div class="input-group mb-2">
								<input type="text" class="form-control" v-model="newText" placeholder="e.g. Hello {name} from {city}, {state}" />
								<button class="btn btn-primary input-group-btn" type="button" aria-label="Add pattern" @click="addPattern(50, 50)">
									<i class="fad fa-plus"></i>
								</button>
							</div>
							<div class="placement-hint"><i class="fad fa-mouse-pointer me-2"></i>Type text, then click the image to place it exactly there. You can drag it afterwards.</div>
							<div class="variable-helper">
								<div class="variable-helper-title"><i class="fad fa-brackets-curly me-2"></i>Available variables</div>
								<button v-for="variable in variables" :key="variable.token" type="button" class="variable-chip" @click="insertVariable(variable.token)">
									<code>{{ variable.token }}</code><span>{{ variable.description }}</span>
								</button>
							</div>
						</div>

						<div v-if="patterns.length > 0" class="pattern-list mb-3">
							<label class="form-label">Existing Patterns</label>
							<div v-for="(pattern, index) in patterns" :key="index" class="pattern-item" :class="{ selected: selectedIndex === index }" @click="selectedIndex = index">
								<div class="pattern-text">
									<strong>{{ pattern.text }}</strong><small>X {{ pattern.x.toFixed(1) }}% · Y {{ pattern.y.toFixed(1) }}%</small>
								</div>
								<div class="pattern-actions">
									<button type="button" class="btn btn-sm btn-outline-danger" aria-label="Remove pattern" @click.stop="removePattern(index)">
										<i class="fad fa-trash"></i>
									</button>
								</div>
							</div>
						</div>

						<div v-if="selectedPattern" class="pattern-styling">
							<h6>Style Selected Pattern</h6>
							<div class="form-group mb-2">
								<label class="form-label">{{ isQr(selectedPattern) ? 'QR Size' : 'Font Size' }}</label>
								<input v-if="isQr(selectedPattern)" type="range" class="form-range" min="80" max="500" v-model.number="selectedPattern.qrSize" />
								<input v-else type="range" class="form-range" min="24" max="120" v-model.number="selectedPattern.fontSize" />
								<small class="form-text">{{ isQr(selectedPattern) ? selectedPattern.qrSize : selectedPattern.fontSize }}px</small>
							</div>

							<template v-if="!isQr(selectedPattern)">
								<div class="form-group mb-2">
									<label class="form-label">Font Weight</label>
									<select class="form-select" v-model="selectedPattern.fontWeight">
										<option value="400">Normal</option>
										<option value="500">Medium</option>
										<option value="600">Semi Bold</option>
										<option value="700">Bold</option>
										<option value="800">Extra Bold</option>
										<option value="900">Black</option>
									</select>
								</div>

								<div class="form-group mb-2">
									<label class="form-label">Color</label>
									<input type="color" class="form-control form-control-color" v-model="selectedPattern.color" />
								</div>

								<div class="form-group mb-2">
									<label class="form-label">Font Family</label>
									<select class="form-select" v-model="selectedPattern.fontFamily">
										<option value="PlusJakartaSans">PlusJakartaSans</option>
									</select>
								</div>
							</template>

							<button type="button" class="pattern-remove-btn" @click="removePattern(selectedIndex)">
								<i class="fad fa-trash me-2"></i>Remove pattern
							</button>
						</div>

						<div class="sidebar-footer mt-4 pt-3 border-top">
							<div class="d-flex gap-2">
								<button type="button" class="pattern-modal-btn flex-fill" data-bs-dismiss="modal">Close</button>
								<button type="button" class="pattern-modal-btn pattern-modal-btn--primary flex-fill" @click="save">Save settings</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from 'bootstrap'
import { PATTERN_VARIABLES, QR_TOKEN, loadTemplateFonts, measurePattern, newPattern, patternPreviewText, patternTextStroke, previewFontFamily } from '@/utils/postcardPatterns'

const IDLE_DRAG = { active: false, index: null, startX: 0, startY: 0, initialX: 0, initialY: 0 }

export default {
	name: 'PostcardPatternEditor',
	props: {
		// Image URLs of both sides; the editor shows the one being edited.
		frontImage: { type: String, default: '' },
		backImage: { type: String, default: '' },
		variables: { type: Array, default: () => PATTERN_VARIABLES },
	},
	data() {
		return {
			modal: null,
			side: 'front',
			patterns: [],
			selectedIndex: null,
			hoveredIndex: null,
			newText: '',
			drag: { ...IDLE_DRAG },
			qrToken: QR_TOKEN,
		}
	},
	computed: {
		modalId() {
			return `postcardPatternEditor-${this._uid}`
		},
		image() {
			return this.side === 'front' ? this.frontImage : this.backImage
		},
		selectedPattern() {
			return this.selectedIndex === null ? null : this.patterns[this.selectedIndex] || null
		},
	},
	watch: {
		selectedPattern: {
			deep: true,
			handler(pattern) {
				if (pattern && !this.isQr(pattern)) measurePattern(pattern)
			},
		},
	},
	mounted() {
		loadTemplateFonts()
		this.modal = new Modal(this.$refs.modal)
		// Overlay sizes depend on the laid-out image width, which is 0 until the
		// modal has finished opening (a cached image never fires @load again).
		this.$refs.modal.addEventListener('shown.bs.modal', this.refreshLayout)
	},
	beforeDestroy() {
		this.$refs.modal.removeEventListener('shown.bs.modal', this.refreshLayout)
		this.stopDrag()
		if (this.modal) this.modal.dispose()
	},
	methods: {
		/** Open the editor for one side with a draft copy of its patterns. */
		open(side, patterns = []) {
			this.side = side
			this.patterns = patterns.map(pattern => ({ ...pattern }))
			this.selectedIndex = null
			this.newText = ''
			this.modal.show()
		},
		refreshLayout() {
			this.$forceUpdate()
		},
		save() {
			this.$emit('save', { side: this.side, patterns: this.patterns.map(pattern => ({ ...pattern })) })
			this.modal.hide()
		},
		isQr(pattern) {
			return pattern.text === QR_TOKEN
		},
		previewText(text) {
			return patternPreviewText(text)
		},
		fontFamily(family) {
			return previewFontFamily(family)
		},
		textStroke(pattern) {
			return patternTextStroke(pattern, this.displaySize(pattern.fontSize))
		},
		// Patterns store sizes in source-image pixels; scale them to the displayed image.
		displaySize(size) {
			const img = this.$refs.templateImage
			if (!img || !img.naturalWidth || !img.clientWidth) return size
			return Math.round(size / (img.naturalWidth / img.clientWidth))
		},
		insertVariable(token) {
			// A QR code is a pattern on its own, it cannot be mixed with text.
			if (token === QR_TOKEN) {
				this.newText = token
				return
			}
			const spacer = this.newText && !this.newText.endsWith(' ') ? ' ' : ''
			this.newText += spacer + token
		},
		handleImageClick(event) {
			if (!this.newText.trim()) {
				this.$swal.fire({ title: 'No Pattern Text', text: 'Please enter pattern text first before placing it on the image.', icon: 'warning' })
				return
			}
			const rect = this.$refs.templateImage.getBoundingClientRect()
			this.addPattern(((event.clientX - rect.left) / rect.width) * 100, ((event.clientY - rect.top) / rect.height) * 100)
		},
		addPattern(x, y) {
			if (!this.newText.trim()) {
				this.$swal.fire({ title: 'Empty Pattern', text: 'Please enter pattern text.', icon: 'warning' })
				return
			}
			this.patterns.push(newPattern(this.newText.trim(), x, y))
			this.selectedIndex = this.patterns.length - 1
			this.newText = ''
		},
		removePattern(index) {
			this.patterns.splice(index, 1)
			if (this.selectedIndex === index) {
				this.selectedIndex = null
			} else if (this.selectedIndex > index) {
				this.selectedIndex -= 1
			}
		},
		startDrag(event, index) {
			event.preventDefault()
			event.stopPropagation()
			this.selectedIndex = index
			const pattern = this.patterns[index]
			this.drag = { active: true, index, startX: event.clientX, startY: event.clientY, initialX: pattern.x, initialY: pattern.y }
			document.addEventListener('mousemove', this.onDragMove)
			document.addEventListener('mouseup', this.stopDrag)
			document.body.style.userSelect = 'none'
			document.body.style.cursor = 'grabbing'
		},
		onDragMove(event) {
			if (!this.drag.active) return
			const rect = this.$refs.templateImage.getBoundingClientRect()
			const pattern = this.patterns[this.drag.index]
			if (!pattern) return
			pattern.x = Math.min(Math.max(this.drag.initialX + ((event.clientX - this.drag.startX) / rect.width) * 100, 2), 95)
			pattern.y = Math.min(Math.max(this.drag.initialY + ((event.clientY - this.drag.startY) / rect.height) * 100, 2), 95)
		},
		stopDrag() {
			if (!this.drag.active) return
			this.drag = { ...IDLE_DRAG }
			document.removeEventListener('mousemove', this.onDragMove)
			document.removeEventListener('mouseup', this.stopDrag)
			document.body.style.userSelect = ''
			document.body.style.cursor = ''
		},
	},
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.template-modal {
	max-width: 95vw !important;
	width: 95vw !important;
	height: 95vh !important;
	margin: 2.5vh auto !important;
}

.template-modal .modal-content {
	height: 100%;
	display: flex;
	flex-direction: column;
	border: 1px solid #e6e8ec;
	border-radius: 14px;
	overflow: hidden;
	box-shadow: 0 18px 55px rgba(31, 41, 55, 0.16);
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.pattern-modal-header { border-bottom: 1px solid #f0f1f4; padding: 0.9rem 1.25rem; background: #fff; }
.pattern-modal-title { display: flex; align-items: center; font-size: 1rem; font-weight: 700; color: #2b303b; letter-spacing: -0.01em; margin-bottom: 2px; }
.pattern-modal-sub { display: block; font-size: 0.78rem; color: #8a909c; line-height: 1.45; }
.pattern-modal-close { margin-left: auto; width: 30px; height: 30px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 7px; background: transparent; color: #2b303b; font-size: 24px; line-height: 1; opacity: 0.65; }
.pattern-modal-close:hover { background: #f3f5f9; color: #171a20; opacity: 1; }

.template-modal-body {
	display: flex;
	flex: 1;
	padding: 0;
	overflow: hidden;
	min-height: 0;
}

.template-image-main-container {
	flex: 1;
	display: flex;
	flex-direction: column;
	position: relative;
	background: #f3f5f9;
	border-right: 1px solid #e6e8ec;
}

.template-image-scroll-container {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: auto;
	padding: 16px;
	max-width: calc(95vw - 420px);
}

.template-image-container {
	position: relative;
	display: inline-block;
	width: fit-content;
	max-width: 100%;
	max-height: 100%;
	flex: 0 0 auto;
	cursor: crosshair;
	overflow: hidden;
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(43, 48, 59, 0.1);
}

.template-preview-image {
	display: block;
	width: auto;
	height: auto;
	max-width: 100%;
	max-height: calc(95vh - 112px);
	min-width: 0;
	object-fit: contain;
}

.template-sidebar {
	width: 420px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	padding: 18px;
	overflow-x: hidden;
	overflow-y: auto;
	background: #fff;
	scrollbar-width: thin;
	scrollbar-color: transparent transparent;
}

.template-sidebar:hover { scrollbar-color: #aeb4bf transparent; }
.pattern-sidebar-heading { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #8a909c; }
.pattern-sidebar-sub { margin: 4px 0 16px; font-size: 12px; color: #8a909c; line-height: 1.45; }
.form-label { font-size: 0.78rem; font-weight: 650; color: #2b303b; }
.form-control,
.form-select { border-color: #e6e8ec; border-radius: 8px; font-size: 0.8rem; box-shadow: none; }
.form-control:focus,
.form-select:focus { border-color: #2b6cff; box-shadow: 0 0 0 2px rgba(43, 108, 255, 0.1); }

.pattern-section .input-group { display: flex; width: 100%; }
.pattern-section .input-group .form-control { flex: 1; border-top-right-radius: 0; border-bottom-right-radius: 0; border-right: 0; }
.input-group-btn { flex-shrink: 0; border-radius: 0 8px 8px 0; background: #2b303b; border-color: #2b303b; }

.placement-hint { padding: 10px 12px; margin: 10px 0; border-radius: 10px; background: #f3f5f9; color: #596170; font-size: 12px; line-height: 1.5; }

.variable-helper { display: grid; gap: 7px; padding: 12px; border: 1px solid #e6e8ec; border-radius: 12px; }
.variable-helper-title { font-size: 12px; font-weight: 700; color: #475569; }
.variable-chip {
	display: grid;
	grid-template-columns: 112px 1fr;
	gap: 8px;
	align-items: center;
	width: 100%;
	padding: 7px 9px;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	background: #fff;
	color: #64748b;
	font-size: 11px;
	text-align: left;
}
.variable-chip:hover { background: #f3f5f9; border-color: #d6dae2; }
.variable-chip code { color: #2b303b; font-weight: 700; }

.text-pattern-overlay {
	position: absolute;
	z-index: 10;
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 0;
	cursor: pointer;
	user-select: none;
	white-space: nowrap;
	background: transparent;
	border: 2px solid transparent;
	border-radius: 4px;
	transition: all 0.2s ease;
}
.text-pattern-overlay:hover { background: #fff; border-color: #3b82f6; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); }
.text-pattern-overlay.selected { background: rgba(59, 130, 246, 0.1); border-color: #3b82f6; box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2); }
.text-pattern-overlay.dragging { z-index: 20; background: rgba(59, 130, 246, 0.2); border-color: #3b82f6; cursor: grabbing !important; }
/* Preview-only GD baseline correction for legacy patterns; stored coordinates stay unchanged. */
.text-pattern-overlay.legacy-coordinate-pattern { transform: translateY(-1.35em); }

.pattern-drag-handle {
	position: absolute;
	top: -9px;
	right: -9px;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	border-radius: 3px;
	background: #3b82f6;
	color: #fff;
	font-size: 10px;
	cursor: grab;
	opacity: 0;
	transition: all 0.2s ease;
}
.pattern-drag-handle.visible { opacity: 1; }
.pattern-drag-handle:hover { background: #2563eb; transform: scale(1.1); }

.pattern-text-content { flex: 1; pointer-events: none; }

.pattern-qr-preview {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 32px;
	min-height: 32px;
	background: #fff;
	border: 2px solid #111827;
	color: #111827;
	font-size: 70%;
	line-height: 1;
	pointer-events: none;
}

.pattern-list { max-height: 200px; overflow-y: auto; }
.pattern-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 6px;
	padding: 8px 12px;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	cursor: pointer;
}
.pattern-item:hover,
.pattern-item.selected { background: #f3f5f9; border-color: #d6dae2; }
.pattern-text { display: flex; flex: 1; flex-direction: column; min-width: 0; font-size: 14px; color: #374151; }
.pattern-text small { margin-top: 2px; color: #94a3b8; font-size: 10px; }
.pattern-actions { display: flex; gap: 4px; }

.pattern-styling { padding: 16px; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 8px; }
.pattern-styling h6 { margin-bottom: 16px; color: #374151; font-weight: 600; }
.form-range { width: 100%; }
.form-control-color { width: 50px; height: 38px; padding: 4px; }

.pattern-remove-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	margin-top: 18px;
	padding: 11px 14px;
	border: 1px solid #fecaca;
	border-radius: 10px;
	background: #fff;
	color: #dc2626;
	font-size: 13px;
	font-weight: 700;
}
.pattern-remove-btn:hover { border-color: #ef4444; background: #fef2f2; }

.sidebar-footer { margin: 0 -18px -18px; padding: 16px 18px 18px; background: #fff; border-top: 1px solid #f0f1f4 !important; }
.pattern-modal-btn { display: inline-flex; align-items: center; justify-content: center; padding: 0.42rem 0.7rem; background: transparent; border: 1px solid #e6e8ec; border-radius: 7px; color: #2b303b; font-size: 0.78rem; font-weight: 600; }
.pattern-modal-btn:hover { background: #f3f5f9; border-color: #d6dae2; }
.pattern-modal-btn--primary { background: #2b303b; border-color: #2b303b; color: #fff; }
.pattern-modal-btn--primary:hover { background: #171a20; border-color: #171a20; }
</style>
